import { useEffect, useState, useRef } from 'react';
import { MapPin, Search, Loader2, Phone } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import TextInput from '../Form/TextInput';
import Button from '../Button/Button';
import NotificationBadge from '../Notifications/NotificationBadge';
import FallbackImage from '../FallbackImage';
import avatar from '/img/avatarnew.png';

const TopBar = ({ logout, userType = 'customer', userData = null }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(true);
  const [userLocation, setUserLocation] = useState('Detecting location...');
  const [isLocationLoading, setIsLocationLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const location = useLocation();

  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const geocoderRef = useRef(null);

  const [mapsApiLoaded, setMapsApiLoaded] = useState(false);

  // Initialize Geocoder when Google Maps API is loaded (handled by APIProvider)
  useEffect(() => {
    const checkMapsApi = () => {
      if (window.google?.maps?.Geocoder && !geocoderRef.current) {
        geocoderRef.current = new window.google.maps.Geocoder();
        setMapsApiLoaded(true);
      }
    };

    // Check immediately
    checkMapsApi();

    // Also check periodically in case API loads after component mounts
    const interval = setInterval(checkMapsApi, 100);

    return () => clearInterval(interval);
  }, []);

  // Fallback to show coordinates when geocoding fails
  const setLocationFallback = (lat, lng) => {
    const roundedLat = parseFloat(lat).toFixed(4);
    const roundedLng = parseFloat(lng).toFixed(4);
    setUserLocation(`${roundedLat}, ${roundedLng}`);
  };

  useEffect(() => {
    if (mapsApiLoaded && geocoderRef.current) {
      getCurrentLocation();
    }
  }, [mapsApiLoaded]);

  const getCurrentLocation = () => {
    try {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            setLatitude(lat);
            setLongitude(lng);
            // Fetch readable address from coordinates
            fetchLocationName(lat, lng);
          },
          (error) => {
            setUserLocation('Location unavailable');
            setIsLocationLoading(false);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000
          }
        );
      } else {
        setUserLocation('Location not supported');
        setIsLocationLoading(false);
      }
    } catch (error) {
      setUserLocation('Location error');
      setIsLocationLoading(false);
    }
  };

  const fetchLocationName = (lat, lng) => {
    if (!geocoderRef.current) {
      setUserLocation('Location service unavailable');
      setIsLocationLoading(false);
      return;
    }

    const latlng = { lat, lng };

    // Use Geocoder service from the library for reverse geocoding
    geocoderRef.current.geocode({ location: latlng }, (results, status) => {

      if (status === 'OK' && results && results.length > 0) {
        // Find the most specific result (prefer street_address, route, or premise)
        let bestResult = results[0];

        for (const result of results) {
          const types = result.types || [];
          // Prefer results with more specific address types
          if (
            types.includes('street_address') ||
            types.includes('route') ||
            types.includes('premise') ||
            types.includes('establishment')
          ) {
            bestResult = result;
            break;
          }
        }

        // Use the formatted_address from geocoding result (most accurate)
        // The geocoding API already provides a well-formatted address
        if (bestResult.formatted_address) {
          setUserLocation(bestResult.formatted_address);
          setIsLocationLoading(false);
        } else {
          // Fallback: extract from components if formatted_address is not available
          const locationString = extractReadableLocation([bestResult]);
          setUserLocation(locationString);
          setIsLocationLoading(false);
        }
      } else {

        // Provide more specific error messages based on the status
        switch (status) {
          case 'REQUEST_DENIED':
            setLocationFallback(lat, lng);
            break;
          case 'OVER_QUERY_LIMIT':
            setUserLocation('Location service temporarily unavailable');
            break;
          case 'ZERO_RESULTS':
            setLocationFallback(lat, lng);
            break;
          case 'INVALID_REQUEST':
            setUserLocation('Invalid location request');
            break;
          default:
            setLocationFallback(lat, lng);
        }
        setIsLocationLoading(false);
      }
    });
  };

  const extractReadableLocation = (results) => {

    // Look for the most specific result (street_address, route, or establishment)
    for (let i = 0; i < results.length; i++) {
      const result = results[i];

      const components = result.address_components;

      // Check if this result has a street address or route (more specific)
      const hasStreetAddress =
        result.types.includes('street_address') ||
        result.types.includes('route') ||
        result.types.includes('establishment') ||
        result.types.includes('premise');

      if (hasStreetAddress) {
        return result.formatted_address;
      }
    }

    // If no specific address found, build address from components of the first result
    const firstResult = results[0];
    const components = firstResult.address_components;

    const streetNumber = components.find((comp) => comp.types.includes('street_number'));
    const route = components.find((comp) => comp.types.includes('route'));
    const sublocality = components.find(
      (comp) => comp.types.includes('sublocality') || comp.types.includes('sublocality_level_1')
    );
    const locality = components.find((comp) => comp.types.includes('locality'));
    const area = components.find((comp) => comp.types.includes('administrative_area_level_2'));
    const state = components.find((comp) => comp.types.includes('administrative_area_level_1'));
    const country = components.find((comp) => comp.types.includes('country'));


    // Build address parts
    const addressParts = [];

    if (streetNumber) addressParts.push(streetNumber.long_name);
    if (route) addressParts.push(route.long_name);
    if (sublocality) addressParts.push(sublocality.long_name);
    if (locality) addressParts.push(locality.long_name);
    if (area && !locality) addressParts.push(area.long_name); // Use area if no locality
    if (state) addressParts.push(state.long_name);
    if (country) addressParts.push(country.long_name);

    if (addressParts.length > 0) {
      const builtAddress = addressParts.join(', ');
      return builtAddress;
    }

    // Final fallback: use formatted_address
    return firstResult.formatted_address;
  };

  return (
    <header className="font-inter bg-white border-b border-gray-100 px-2 sm:px-4 md:px-6 py-4 sm:py-5">
      <div className="flex w-full items-center gap-2 sm:gap-4 md:gap-6 lg:gap-8 justify-between">
        {/* Location on the left */}
        <Button
          variant="ghost"
          className="flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-3.5 text-xs sm:text-base min-w-[120px]"
        >
          {isLocationLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
            </>
          ) : (
            <>
              <MapPin size={20} />
              <span className="sm:inline">{userLocation}</span>
            </>
          )}
        </Button>

        {/* Notifications and Profile on the right */}
        <div className="flex items-center gap-2 sm:gap-4 md:gap-6 lg:gap-8">
          <div className="relative flex-shrink-0">
            <Link to={`/${userType}/notifications`} state={{ backgroundLocation: location }} className="block">
              <NotificationBadge />
            </Link>
          </div>

          <div className="relative flex-shrink-0">
            <button
              className="flex items-center p-1.5 rounded-full bg-gray-200 hover:bg-gray-300 focus:outline-none transition-colors"
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              style={{ minWidth: '40px', minHeight: '40px' }}
            >
              <FallbackImage
                className="h-8 w-8 min-w-[2rem] min-h-[2rem] rounded-full border border-gray-200 object-cover"
                src={userData?.imageUrl || avatar}
                alt="User avatar"
                fallbackSrc={avatar}
                clickable={false}
              />
            </button>

            {/* Dropdown menu */}
            {isProfileOpen && (
              <div className="origin-top-right absolute right-0 mt-1 w-max rounded-lg bg-white border border-gray-100 focus:outline-none z-20 py-1">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">
                    {userData?.firstName || 'User'} {userData?.lastName || ''}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{userData?.email || 'No email'}</p>
                  {userData?.userAddress && (
                    <p className="text-xs text-gray-400 mt-1">
                      {userData.userAddress.state}, {userData.userAddress.country}
                    </p>
                  )}
                </div>
                <div className="py-1">
                  <a
                    href={userType === 'artisan' ? '/artisan/profile&settings' : '/customer/profile&settings'}
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    Profile
                  </a>
                </div>
                <div className="py-1 border-t border-gray-100">
                  <a
                    href="tel:+2347001340054"
                    className="flex items-center px-4 py-2 text-sm text-blue-600 hover:bg-gray-50"
                  >
                    <Phone size={16} className="mr-2" />
                    <span>+234 700 134 0054</span>
                  </a>
                  <p className="px-4 py-1 text-xs text-gray-500">Need help? Call us</p>
                </div>
                <div
                  className="py-1 border-t border-gray-100"
                  onClick={() => {
                    logout();
                  }}
                >
                  <button className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-50">
                    <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                      />
                    </svg>
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default TopBar;
